import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Check, X } from 'lucide-react';
import { ShieldCheck, ArrowRight, Star, CaretDown, CheckCircle, Lightning, Users, Buildings, Scales, Certificate, Lightbulb } from '@phosphor-icons/react';
import { submitLeadWithGeoLocation, getCountryInfo } from '../services/centralLeadApi';
import countryCodes from '../data/countryCodes';
import { toast } from 'sonner';
import { getServiceData } from '../data/allServicesData';
import PricingSectionV2 from '../components/PricingSectionV2';
import {
  selfFilingProcessCards,
  attorneyLedProcessCards,
  selfFilingPlans,
  attorneyLedPlans,
} from '../mock';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const CROSS_LINKS = [
  { key: 'trademark', label: 'Trademark Registration', path: '/services/trademark/search-registration', icon: ShieldCheck },
  { key: 'copyright', label: 'Copyright Protection', path: '/services/copyright/registration', icon: Certificate },
  { key: 'patent', label: 'Patent Services', path: '/services/patent/searches', icon: Lightbulb },
  { key: 'company', label: 'Business Formation', path: '/services/company/llc', icon: Buildings },
];

const ServiceDetail = ({ onOpenModal }) => {
  const { category, serviceSlug } = useParams();
  const isSearchRegistration = category === 'trademark' && serviceSlug === 'search-registration';
  const content = getServiceData(serviceSlug, category);

  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState({ email: '', phone: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [countryCode, setCountryCode] = useState('+1');
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [howWeWorkRef] = useEmblaCarousel({ loop: true, align: 'start' });
  const [whyChooseUsRef] = useEmblaCarousel({ loop: true, align: 'start' });

  useEffect(() => {
    (async () => {
      try {
        const info = await getCountryInfo();
        if (info) { setDetectedCountry(info); const match = countryCodes.find(c => c.country === info.countryCode); if (match) setCountryCode(match.code); }
      } catch (e) { console.error('Geo error:', e); }
    })();
  }, []);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => phone.replace(/[^\d]/g, '').length === 10;

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };
    if (!validateEmail(formData.email)) { newErrors.email = 'Please enter a valid email'; hasErrors = true; }
    if (!validatePhone(formData.phone)) { newErrors.phone = 'Phone must be exactly 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); toast.error('Please fix errors'); return; }
    setIsLoading(true);
    try {
      const leadData = { name: formData.name, email: formData.email, phone: formData.phone, countryCode, country: countryCodes.find(c => c.code === countryCode)?.country || 'US', description: formData.message || `Service inquiry: ${content?.title}` };
      const result = await submitLeadWithGeoLocation(leadData, null, `Service: ${content?.title}`);
      if (result.success) { toast.success(result.message || 'Request submitted!'); setFormData({ name: '', email: '', phone: '', message: '' }); setErrors({ email: '', phone: '' }); }
      else { toast.error(result.message || 'Submission failed.'); setIsLoading(false); }
    } catch (error) { console.error('Error:', error); toast.error('Please try again.'); setIsLoading(false); }
  };

  if (!content) return <div className="min-h-screen pt-[72px] flex items-center justify-center"><p className="text-slate-500">Service not found.</p></div>;

  return (
    <div className="min-h-screen pt-[72px]">

      {/* 1. BANNER SECTION */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={content.bannerImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-5">{content.punchLine}</p>
              <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-heading font-semibold text-white tracking-tighter leading-[1.05] mb-6">{content.mainHeading}</h1>
              <p className="text-base text-slate-300 leading-relaxed max-w-lg mb-8">{content.heroParagraph}</p>
              {/* Hero bullets - only for monitoring service */}
              {serviceSlug === 'monitoring' && content.heroBullets && (
                <ul className="space-y-3 mb-8">
                  {content.heroBullets.map((bullet, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-300 text-sm">
                      <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" weight="fill" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              )}
              {/* Cross-service links */}
              <div className="flex flex-wrap gap-3 pt-2">
                {CROSS_LINKS.filter(l => l.key !== category).map(l => {
                  const Icon = l.icon;
                  return (
                    <Link key={l.key} to={l.path} data-testid={`cross-link-${l.key}`}
                      className="group flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm hover:bg-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all duration-300">
                      <Icon className="h-4 w-4 text-[#D4AF37]" weight="bold" />
                      <span className="text-xs font-semibold text-white/80 group-hover:text-white transition-colors">Need {l.label}?</span>
                      <ArrowRight className="h-3 w-3 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Form CTA */}
            <div data-testid="banner-form" className="backdrop-blur-xl bg-white/95 border border-white/20 shadow-[0_20px_60px_rgba(0,0,0,0.3)] rounded-lg p-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-heading font-bold text-slate-900 mb-0.5">{content.formCTA?.heading || 'Get Started Today'}</h2>
                  <p className="text-sm text-[#044B3B] font-semibold">{content.formCTA?.offer || 'Free Consultation'}</p>
                </div>
                {/* <div className="w-11 h-11 bg-[#044B3B] rounded-lg flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-white" weight="bold" />
                </div> */}
              </div>
              <p className="text-sm text-slate-500 mb-5 leading-relaxed">{content.formCTA?.description || 'Fill out the form for a free expert consultation.'}</p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input data-testid="banner-name" type="text" placeholder="Full Name *" value={formData.name} onChange={(e) => setFormData(p => ({...p, name: e.target.value}))} required className="border-slate-200 focus:border-[#044B3B] rounded-md text-sm" />
                <div>
                  <Input data-testid="banner-email" type="email" placeholder="Email Address *" value={formData.email}
                    onChange={(e) => { setFormData(p => ({...p, email: e.target.value})); if (errors.email && validateEmail(e.target.value)) setErrors(p => ({...p, email: ''})); }}
                    onBlur={() => { if (formData.email && !validateEmail(formData.email)) setErrors(p => ({...p, email: 'Valid email required'})); else setErrors(p => ({...p, email: ''})); }}
                    className={`${errors.email ? 'border-red-500' : 'border-slate-200'} focus:border-[#044B3B] rounded-md text-sm`} required />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <div className="flex gap-2">
                    <Select value={countryCode} onValueChange={setCountryCode}>
                      <SelectTrigger data-testid="banner-country" className="w-24 px-2 py-2 border border-slate-200 rounded-md bg-white text-xs font-medium focus:border-[#044B3B]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {countryCodes.map((c, i) => (<SelectItem key={i} value={c.code}>{c.flag} {c.code}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Input data-testid="banner-phone" type="tel" placeholder="Phone (10 digits) *" value={formData.phone}
                      onChange={(e) => { const v = e.target.value.replace(/[^0-9\s\-]/g,''); const d = v.replace(/[^\d]/g,''); if(d.length>10)return; setFormData(p=>({...p,phone:v})); if(errors.phone&&d.length===10)setErrors(p=>({...p,phone:''})); }}
                      onBlur={() => { const d=formData.phone.replace(/[^\d]/g,''); if(formData.phone&&d.length!==10) setErrors(p=>({...p,phone:'10 digits required'})); else setErrors(p=>({...p,phone:''})); }}
                      className={`flex-1 ${errors.phone?'border-red-500':'border-slate-200'} focus:border-[#044B3B] rounded-md text-sm`} required />
                  </div>
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
                <Textarea data-testid="banner-message" placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)" value={formData.message} onChange={(e) => setFormData(p => ({...p, message: e.target.value}))} rows={3} className="border-slate-200 focus:border-[#044B3B] rounded-md text-sm resize-none" />
                <Button data-testid="banner-submit" type="submit" disabled={isLoading} className="w-full bg-[#044B3B] hover:bg-[#03392D] text-white font-semibold text-sm py-5 rounded-md">
                  {isLoading ? 'Submitting...' : <span className="flex items-center justify-center gap-2">{content.formCTA?.buttonText || 'Get Free Consultation'} <ArrowRight className="h-4 w-4" /></span>}
                </Button>
              </form>
              <p className="text-xs text-slate-400 text-center mt-3">Secure & confidential. No obligation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHY YOU NEED THIS SERVICE */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="rounded-lg overflow-hidden shadow-2xl order-2 lg:order-1">
              <img src={content.whyNeedImage} alt={content.whyNeedHeading} className="w-full h-[450px] object-cover" />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">Why You Need This</p>
              <h2 className="text-3xl sm:text-4xl font-heading font-medium text-slate-900 tracking-tight mb-6">{content.whyNeedHeading}</h2>
              <p className="text-base text-slate-600 leading-relaxed">{content.whyNeedParagraph}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. HOW WE WORK */}
      {!isSearchRegistration && (
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-3">How We Work</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-medium text-white tracking-tight mb-4">{content.howWeWorkHeading || 'Our Proven Process'}</h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">{content.howWeWorkParagraph || 'A streamlined 3-step approach to deliver results efficiently.'}</p>
          </div>
          {/* Grid for desktop, slider for mobile */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-xs:hidden">
            {(content.process || []).map((step, i) => (
              <div key={i} className="text-center group">
                <div className="w-16 h-16 bg-[#044B3B] rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl font-heading font-bold text-white">{i + 1}</span>
                </div>
                <h3 className="text-xl font-heading font-semibold text-white mb-3">{step.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Slider for mobile (576px and below) */}
          <div className="overflow-hidden xs:hidden" ref={howWeWorkRef}>
            <div className="flex gap-4">
              {(content.process || []).map((step, i) => (
                <div key={i} className="flex-[0_0_85%] text-center group">
                  <div className="w-16 h-16 bg-[#044B3B] rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-2xl font-heading font-bold text-white">{i + 1}</span>
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-white mb-3">{step.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* 4. PRICING / PACKAGES */}
      {isSearchRegistration ? (
        <div id="pricing">
          <PricingSectionV2
            onOpenModal={onOpenModal}
            selfFilingProcessCards={selfFilingProcessCards}
            attorneyLedProcessCards={attorneyLedProcessCards}
            selfFilingPlans={selfFilingPlans}
            attorneyLedPlans={attorneyLedPlans}
          />
        </div>
      ) : (
        <section className="py-24 bg-white" id="pricing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {content.pricing === 'quote' ? (
              <div className="text-center max-w-2xl mx-auto">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">Pricing</p>
                <h2 className="text-3xl font-heading font-medium text-slate-900 tracking-tight mb-4">{content.pricingHeading || 'Get a Quote'}</h2>
                <p className="text-sm text-slate-600 mb-8">{content.pricingParagraph || 'Contact us for a personalized quote.'}</p>
                <Button onClick={() => onOpenModal && onOpenModal(`Get a Quote - ${content.title}`)} className="bg-[#044B3B] hover:bg-[#03392D] text-white font-semibold px-8 py-5 rounded-md" data-testid="get-quote-btn">
                  <span className="flex items-center gap-2">{content.pricingButtonText || 'Request a Quote'} <ArrowRight className="h-4 w-4" /></span>
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-14">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">Pricing</p>
                  <h2 className="text-3xl sm:text-4xl font-heading font-medium text-slate-900 tracking-tight mb-4">{content.pricingHeading || `${content.title} Packages`}</h2>
                  <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">{content.pricingParagraph || 'Transparent, flat-rate pricing with no hidden fees.'}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {(content.pricing || []).map((plan, index) => (
                    <Card key={index} className={`relative overflow-hidden rounded-lg ${plan.popular ? 'border-2 border-[#044B3B] shadow-[0_0_24px_rgba(4,75,59,0.15)]' : 'border border-slate-200 hover:border-[#044B3B]/30 hover:shadow-lg'} transition-all duration-300`}>
                      {plan.popular && <div className="absolute top-0 left-0 right-0 bg-[#044B3B] text-white text-xs font-bold text-center py-1.5 uppercase tracking-wider">Most Popular</div>}
                      <div className={`p-6 flex flex-col ${plan.popular ? 'pt-10' : ''}`}>
                        <h3 className="text-lg font-heading font-bold text-slate-900 mb-2">{plan.name}</h3>
                        <div className="mb-4"><span className="text-4xl font-heading font-semibold text-slate-900">${plan.price}</span><span className="text-slate-500 text-sm ml-1">+USPTO fees</span></div>
                        <div className="flex-1 overflow-y-auto max-h-60 mb-6 pr-2 scrollbar-thin">
                          <ul className="space-y-2.5">
                            {plan.features.map((f, i) => (<li key={i} className="flex items-start gap-2"><Check className="h-4 w-4 text-[#044B3B] flex-shrink-0 mt-0.5" /><span className="text-sm text-slate-600">{f}</span></li>))}
                          </ul>
                        </div>
                        <Button onClick={() => onOpenModal && onOpenModal(`${plan.name} - ${content.title}`)} className={`w-full font-semibold text-sm rounded-md mt-auto ${plan.popular ? 'bg-[#044B3B] hover:bg-[#03392D] text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}>Get Started</Button>
                        <p className="text-xs text-slate-400 text-center mt-2">*(Govt. fees not included)</p>
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* 5. WHY CHOOSE US */}
      <section className="py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">Why Choose Us</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-medium text-slate-900 tracking-tight mb-4">{content.whyChooseHeading || 'Why Clients Trust Us'}</h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">{content.whyChooseParagraph || 'Experience, transparency, and results that speak for themselves.'}</p>
          </div>
          {/* Grid for desktop, slider for mobile */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-xs:hidden">
            {(content.usps || []).map((usp, i) => {
              const icons = [ShieldCheck, Lightning, Users];
              const Icon = icons[i % 3];
              return (
                <Card key={i} data-testid={`usp-${i}`} className="p-8 border border-slate-200 hover:border-[#044B3B]/30 hover:bg-[#044B3B] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 rounded-lg bg-white group">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#044B3B] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="h-6 w-6 text-white" weight="regular" />
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-slate-900 group-hover:text-white">{usp.title}</h3>
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed group-hover:text-white/90">{usp.desc}</p>
                </Card>
              );
            })}
          </div>

          {/* Slider for mobile (576px and below) */}
          <div className="overflow-hidden xs:hidden" ref={whyChooseUsRef}>
            <div className="flex gap-4">
              {(content.usps || []).map((usp, i) => {
                const icons = [ShieldCheck, Lightning, Users];
                const Icon = icons[i % 3];
                return (
                  <Card key={i} data-testid={`usp-${i}`} className="flex-[0_0_85%] p-8 border border-slate-200 hover:border-[#044B3B]/30 hover:bg-[#044B3B] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 rounded-lg bg-white group">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#044B3B] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-white" weight="regular" />
                      </div>
                      <h3 className="text-lg font-heading font-semibold text-slate-900 group-hover:text-white">{usp.title}</h3>
                    </div>
                    <p className="text-sm text-slate-500 leading-relaxed group-hover:text-white/90">{usp.desc}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 5.5 COMPARISON TABLE (Enhanced services only) */}
      {content.comparison && content.comparison.length > 0 && (
        <section className="py-24 bg-white" data-testid="comparison-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">How We Compare</p>
              <h2 className="text-3xl sm:text-4xl font-heading font-medium text-slate-900 tracking-tight mb-4">{content.comparisonHeading || 'Us vs. Other Providers'}</h2>
              <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">{content.comparisonParagraph || 'See why thousands of clients choose us over the competition.'}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-lg">
              <div className="grid grid-cols-3 bg-slate-900 text-white">
                <div className="p-4 text-sm font-semibold">Feature</div>
                <div className="p-4 text-sm font-semibold text-center bg-[#044B3B]">Us</div>
                <div className="p-4 text-sm font-semibold text-center">Others</div>
              </div>
              {content.comparison.map((row, i) => (
                <div key={i} className={`grid grid-cols-3 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} border-t border-slate-100`}>
                  <div className="p-4 text-sm text-slate-700 font-medium">{row.feature}</div>
                  <div className="p-4 flex items-center justify-center bg-[#044B3B]/5">
                    {typeof row.us === 'boolean' ? (
                      row.us ? <CheckCircle className="h-5 w-5 text-[#044B3B]" weight="bold" /> : <X className="h-5 w-5 text-slate-300" />
                    ) : <span className="text-sm font-semibold text-[#044B3B]">{row.us}</span>}
                  </div>
                  <div className="p-4 flex items-center justify-center">
                    {typeof row.others === 'boolean' ? (
                      row.others ? <CheckCircle className="h-5 w-5 text-slate-400" weight="bold" /> : <X className="h-5 w-5 text-slate-300" />
                    ) : <span className="text-sm text-slate-500">{row.others}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 6. CTA SECTION */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={content.ctaImage || content.bannerImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#044B3B]/90"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-medium text-white tracking-tight mb-6">{content.ctaHeading || 'Ready to Get Started?'}</h2>
          <p className="text-base text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">{content.ctaParagraph || 'Take the first step toward protecting your intellectual property today.'}</p>
          <Button data-testid="cta-btn" onClick={() => onOpenModal && onOpenModal(`CTA - ${content.title}`)}
            className="bg-white text-[#044B3B] hover:bg-slate-100 font-semibold text-sm px-4 py-5 rounded-md">
            <span className="flex items-center gap-2">{content.ctaButtonText || 'Start Now'} <ArrowRight className="h-4 w-4" /></span>
          </Button>
        </div>
      </section>

      {/* 7. TESTIMONIALS */}
      <TestimonialsSlider testimonials={content.testimonials || []} />

      {/* 8. FAQs */}
      {content.faqs && content.faqs.length > 0 && (
        <section className="py-24 bg-[#FAFAFA]" data-testid="service-faq">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">FAQ</p>
              <h2 className="text-3xl font-heading font-medium text-slate-900 tracking-tight">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-3">
              {content.faqs.map((faq, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left" data-testid={`faq-${i}`}>
                    <span className="text-sm font-semibold text-slate-900 pr-4">{faq.q}</span>
                    <CaretDown className={`h-4 w-4 text-slate-500 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5 -mt-1"><p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

const TestimonialsSlider = ({ testimonials }) => {
  const [emblaRef] = useEmblaCarousel({ loop: true, align: 'start', duration: 45 }, [Autoplay({ delay: 3500, stopOnInteraction: false })]);
  if (!testimonials || testimonials.length === 0) return null;
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">Client Success Stories</p>
          <h2 className="text-3xl font-heading font-medium text-slate-900 tracking-tight">What Our Clients Say</h2>
        </div>
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-5">
            {testimonials.map((t, i) => (
              <div key={i} className="flex-[0_0_100%] md:flex-[0_0_calc(50%-10px)] lg:flex-[0_0_calc(33.333%-14px)] min-w-0">
                <Card className="p-7 h-full border border-slate-200 rounded-lg">
                  <div className="flex gap-0.5 mb-3">{[...Array(5)].map((_, j) => (<Star key={j} className="h-4 w-4 text-[#D4AF37]" weight="fill" />))}</div>
                  <p className="text-sm text-slate-600 mb-5 leading-relaxed italic">&ldquo;{t.content}&rdquo;</p>
                  <div className="pt-4 border-t border-slate-100 mt-auto">
                    <h4 className="text-sm font-semibold text-slate-900">{t.name}</h4>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceDetail;
